// src/components/capabilities-v2/StackingCard.js
// Stacking card component with proper overlapping behavior

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import { Brain, BarChart3, Code2, Sparkles, Calendar, FileText, Wrench, Zap, Activity, Search, Settings, AlertCircle, Layout, Cloud, Shield, Layers, X } from 'lucide-react';

// Icon mappings
const iconMap = { Brain, BarChart3, Code2 };
const bulletIconMap = { Calendar, FileText, Wrench, Zap, Activity, Search, Settings, AlertCircle, Layout, Cloud, Shield, Layers };

const StackingCard = ({ pillar, index, scrollYProgress, totalCards }) => {
  const IconComponent = iconMap[pillar.icon];
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Modal handlers
  const handleImageClick = () => setIsModalOpen(true);
  const handleModalClose = () => setIsModalOpen(false);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsModalOpen(false);
    };
    if (isModalOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  // ============================================
  // CARD ANIMATION TIMING (for offset ["start end", "end start"])
  // Text moves left at 38-48%
  // Cards container fades in at 44-50%
  // Cards appear at 48%, 62%, 72%
  // ============================================

  // Card timing - spread cards across 48% to 82% of scroll
  const cardRanges = [
    { start: 0.48, peak: 0.56 },   // Card 0: 48-56%
    { start: 0.62, peak: 0.70 },   // Card 1: 62-70%
    { start: 0.72, peak: 0.80 },   // Card 2: 72-80%
  ];
  const { start: cardStart, peak: cardPeak } = cardRanges[index] || cardRanges[0];

  // Stack offset - cards stack with slight vertical offset to show layering
  const stackOffset = index * 8;

  // Card entrance: slides up from below
  const y = useTransform(
    scrollYProgress,
    [cardStart, cardPeak],
    [200, stackOffset]
  );

  // Card opacity: fades in
  const opacity = useTransform(
    scrollYProgress,
    [cardStart, cardStart + 0.04],
    [0, 1]
  );

  // Card scale: starts slightly smaller, scales to full
  const scale = useTransform(
    scrollYProgress,
    [cardStart, cardPeak],
    [0.92, 1]
  );

  // Previous cards: when next card appears, current card scales down slightly
  const nextCardRange = cardRanges[index + 1];
  const nextCardStart = nextCardRange ? nextCardRange.start : 0.90; // No scale-down for last card until exit
  const scaleDown = useTransform(
    scrollYProgress,
    [nextCardStart, nextCardStart + 0.08],
    [1, 0.95]
  );

  // Combine scales: entrance scale * scale-down when becoming previous
  const combinedScale = useTransform(
    [scale, scaleDown],
    ([s, sd]) => s * sd
  );

  // Darken overlay for previous cards
  const overlayOpacity = useTransform(
    scrollYProgress,
    [nextCardStart, nextCardStart + 0.08],
    [0, 0.3]
  );

  // Smooth springs
  const smoothY = useSpring(y, { stiffness: 80, damping: 25 });
  const smoothOpacity = useSpring(opacity, { stiffness: 100, damping: 30 });
  const smoothScale = useSpring(combinedScale, { stiffness: 100, damping: 30 });
  const smoothOverlay = useSpring(overlayOpacity, { stiffness: 100, damping: 30 });

  return (
    <>
      <motion.div
        className="stack-card"
        style={{
          y: smoothY,
          opacity: smoothOpacity,
          scale: smoothScale,
          zIndex: index + 1,
          '--card-accent': pillar.accent,
        }}
      >
        {/* Darkening overlay for previous cards */}
        <motion.div
          className="stack-card__overlay"
          style={{ opacity: smoothOverlay }}
        />

        <div className="stack-card__inner">
          {/* Header */}
          <div className="stack-card__header">
            <div className="stack-card__label">
              <IconComponent size={16} />
              <span>{pillar.label}</span>
            </div>
            <h3 className="stack-card__title">{pillar.title}</h3>
          </div>

          {/* Content: Two columns - bullets left, image right */}
          <div className="stack-card__content">
            {/* Bullets */}
            <div className="stack-card__bullets">
              <ul>
                {pillar.bullets.map((bullet, i) => {
                  const BulletIcon = bulletIconMap[pillar.bulletIcons?.[i]] || Sparkles;
                  return (
                    <li key={i}>
                      <BulletIcon size={14} className="bullet-icon" />
                      <span>{bullet}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="stack-card__tags">
                {pillar.tags.map((tag, i) => (
                  <span key={i} className="stack-card__tag">{tag}</span>
                ))}
              </div>
            </div>

            {/* Image */}
            <div className="stack-card__image-zone">
              <div
                className="stack-card__image-frame"
                onClick={handleImageClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleImageClick()}
              >
                <img
                  src={pillar.image}
                  alt={pillar.title}
                  className="stack-card__image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const placeholder = e.target.parentElement.querySelector('.stack-card__placeholder');
                    if (placeholder) placeholder.style.display = 'flex';
                  }}
                />
                <div className="stack-card__placeholder">
                  <IconComponent size={40} />
                  <span>Image Coming Soon</span>
                </div>
                <div className="stack-card__image-hover">
                  <span>Click to expand</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Image Modal */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              className="stack-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={handleModalClose}
            >
              <div className="stack-modal__glow" />
              <button
                className="stack-modal__close"
                onClick={handleModalClose}
                aria-label="Close modal"
              >
                <X size={24} />
              </button>
              <motion.div
                className="stack-modal__content"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
              >
                <img src={pillar.image} alt={pillar.title} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default StackingCard;
